import React from 'react';

import { Box, ButtonBase, Chip, Stack, Typography } from '@mui/material';

interface Props {
  category: string;
  status: boolean | null;
  activeLabel: string;
  clearLabel: string;
  selected: boolean;
  onClick: () => void;
}

export default function StatusColumn({ category, status, activeLabel, clearLabel, selected, onClick }: Props) {
  const isClickable = status !== false;
  const helperText = isClickable ? 'Click to view details' : 'No action needed';

  const chipProps =
    status === null
      ? { label: 'Unknown', color: 'default' as const, variant: 'outlined' as const }
      : status
      ? { label: activeLabel, color: 'error' as const, variant: 'filled' as const }
      : { label: clearLabel, color: 'success' as const, variant: 'outlined' as const };

  return (
    <ButtonBase
      onClick={isClickable ? onClick : undefined}
      disabled={!isClickable}
      focusRipple={isClickable}
      sx={{
        flex: 1,
        minWidth: 0,
        borderRadius: 1,
        display: 'block',
        textAlign: 'center',
        alignSelf: 'stretch',
        '&.Mui-disabled': {
          opacity: 1,
        },
      }}
    >
      <Box
        sx={{
          p: 2,
          borderRadius: 1,
          border: 1,
          borderColor: selected ? 'primary.main' : isClickable ? 'text.secondary' : 'divider',
          cursor: isClickable ? 'pointer' : 'default',
          bgcolor: selected ? 'action.selected' : 'background.paper',
          boxShadow: selected ? 2 : 0,
          transform: 'translateY(0)',
          transition: 'background-color 0.15s, border-color 0.15s, box-shadow 0.15s, transform 0.15s',
          '&:hover': isClickable
            ? {
                bgcolor: selected ? 'action.selected' : 'action.hover',
                borderColor: selected ? 'primary.main' : 'text.primary',
                boxShadow: 2,
                transform: 'translateY(-1px)',
              }
            : undefined,
          '.MuiButtonBase-root:focus-visible &': isClickable
            ? {
                borderColor: 'primary.main',
                boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}33`,
              }
            : undefined,
        }}
      >
        <Stack alignItems="center" spacing={1}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={500}
            textTransform="uppercase"
            letterSpacing={0.5}
          >
            {category}
          </Typography>
          <Chip {...chipProps} size="small" sx={{ fontWeight: 500, height: 24 }} />
          <Typography
            variant="caption"
            color={isClickable ? (selected ? 'primary.main' : 'text.secondary') : 'text.disabled'}
            fontWeight={isClickable ? 500 : 400}
          >
            {helperText}
          </Typography>
        </Stack>
      </Box>
    </ButtonBase>
  );
}
